// Utility functions for handling images in the application

/**
 * Convert a server image path to a blob URL using base64 endpoint
 * @param {string} imagePath - The server path to the image
 * @param {string} apiUrl - The API base URL
 * @returns {Promise<string>} - Promise that resolves to a blob URL
 */
export const convertImagePathToBlob = async (imagePath, apiUrl) => {
  if (!imagePath) return null;

  try {
    // Remove '/api' from the end of the API URL to get the base server URL
    const baseUrl = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;

    // Use the new base64 endpoint with query parameter
    const encodedPath = encodeURIComponent(imagePath);
    const response = await fetch(`${baseUrl}/api/images?path=${encodedPath}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const data = await response.json();

    // Convert base64 data URL to blob
    const base64Response = await fetch(data.dataUrl);
    const blob = await base64Response.blob();

    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error converting image path to blob:', error);
    return null;
  }
};

/**
 * Convert multiple image paths to blob URLs
 * @param {string[]} imagePaths - Array of server paths to images
 * @param {string} apiUrl - The API base URL
 * @returns {Promise<string[]>} - Promise that resolves to an array of blob URLs
 */
export const convertImagePathsToBlobs = async (imagePaths, apiUrl) => {
  if (!imagePaths || imagePaths.length === 0) return [];

  const blobPromises = imagePaths.map((path) =>
    convertImagePathToBlob(path, apiUrl)
  );
  const blobUrls = await Promise.all(blobPromises);
  return blobUrls.filter((url) => url !== null);
};

/**
 * Clean up blob URLs to prevent memory leaks
 * @param {string|string[]} blobUrls - Single blob URL or array of blob URLs
 */
export const revokeBlobUrls = (blobUrls) => {
  if (!blobUrls) return;

  const urls = Array.isArray(blobUrls) ? blobUrls : [blobUrls];
  urls.forEach((url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
};

/**
 * Check if a URL is a blob URL
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it's a blob URL
 */
export const isBlobUrl = (url) => {
  return url && url.startsWith('blob:');
};

/**
 * Convert a File object to a blob URL
 * @param {File} file - The file object
 * @returns {string} - The blob URL
 */
export const fileToBlobUrl = (file) => {
  if (!file) return null;
  return URL.createObjectURL(file);
};

/**
 * Convert base64 data URL to blob URL
 * @param {string} dataUrl - The base64 data URL
 * @returns {Promise<string>} - Promise that resolves to a blob URL
 */
export const dataUrlToBlobUrl = async (dataUrl) => {
  try {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error converting data URL to blob:', error);
    return null;
  }
};
